import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { MotionConfig } from 'framer-motion';
import { AuthProvider, useAuth, ROLES, HOME_FOR } from './context/AuthContext';
import { ToastProvider } from './context/ToastContext';
import { EventProvider } from './context/EventContext';
import Layout from './components/Layout';
import { Loader } from './components/UI';

import Login from './pages/Login';
import PublicRegistration from './pages/public/PublicRegistration';
import CheckInLink from './pages/public/CheckInLink';
import LuggageTag from './pages/public/LuggageTag';
import NotFound from './pages/NotFound';

/* Super admin */
import AdminDashboard from './pages/admin/Dashboard';
import Events from './pages/admin/Events';
import EventDetail from './pages/admin/EventDetail';
import EventSheet from './pages/admin/EventSheet';
import Corporates from './pages/admin/Corporates';
import Hotels from './pages/admin/Hotels';
import Participants from './pages/shared/Participants';
import Messages from './pages/shared/Messages';
import AttendeeDeclaration from './pages/attendee/Declaration';
import DeclarationGate from './components/DeclarationGate';
import Settings from './pages/admin/Settings';
import StaffAccounts from './pages/admin/StaffAccounts';
import Registrations from './pages/admin/Registrations';

/* Shared operational screens (super admin + corporate) */
import Rooms from './pages/shared/Rooms';
import Flights from './pages/shared/Flights';
import Transport from './pages/shared/Transport';
import LuggagePage from './pages/shared/Luggage';
import Attendance from './pages/shared/Attendance';
import Notifications from './pages/shared/Notifications';
import Reports from './pages/shared/Reports';

/* Corporate */
import CorporateDashboard from './pages/corporate/Dashboard';

/* Staff */
import StaffDashboard from './pages/staff/Dashboard';
import ScanDesk from './pages/staff/ScanDesk';

/* Attendee — the app's experience, laid out for the web */
import AttendeeHome from './pages/attendee/Home';
import AttendeeSchedule from './pages/attendee/Schedule';
import AttendeePass from './pages/attendee/Pass';
import AttendeeCheckIn from './pages/attendee/CheckIn';
import AttendeeTravel from './pages/attendee/Travel';
import AttendeeStay from './pages/attendee/Stay';
import AttendeeTransport from './pages/attendee/Transport';
import AttendeeLuggage from './pages/attendee/Luggage';
import AttendeeInbox from './pages/attendee/Inbox';
import AttendeeProfile from './pages/attendee/Profile';

/* Hotel */
import HotelDashboard from './pages/hotel/Dashboard';
import HotelRooms from './pages/hotel/Rooms';
import CheckInDesk from './pages/hotel/CheckInDesk';
import HotelReport from './pages/hotel/Report';

/** Gate that also enforces the role a branch of the tree belongs to. */
function Protected({ roles, gate: Gate }) {
  const { user, loading } = useAuth();
  if (loading) return <Loader full label="Restoring your session" />;
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to={HOME_FOR[user.role] || '/login'} replace />;
  return Gate ? <Layout gate={Gate} /> : <Layout />;
}

/** Sends an already-signed-in user to their own portal. */
function RoleHome() {
  const { user, loading } = useAuth();
  if (loading) return <Loader full />;
  return <Navigate to={user ? (HOME_FOR[user.role] || '/login') : '/login'} replace />;
}

export default function App() {
  return (
    // reducedMotion="user" makes every motion component settle at its final
    // value for viewers whose OS asks for reduced motion.
    <MotionConfig reducedMotion="user">
      <BrowserRouter>
        <ToastProvider>
          <AuthProvider>
          <EventProvider>
            <Routes>
              <Route path="/" element={<RoleHome />} />
              <Route path="/login" element={<Login />} />
              <Route path="/register/:slug" element={<PublicRegistration />} />
              {/* Check-in from the desk's QR code or a shared link — no sign-in. */}
              <Route path="/checkin/:slug" element={<CheckInLink />} />
              {/* What a printed bag tag opens — no account needed. */}
              <Route path="/luggage/:tag" element={<LuggageTag />} />

              {/* ── Super Admin ─────────────────────────────────────── */}
              <Route path="/admin" element={<Protected roles={[ROLES.SUPER_ADMIN]} />}>
                <Route index element={<AdminDashboard />} />
                {/* Admins scan at the desk too — luggage tagging, entry, sessions. */}
                <Route path="scan" element={<ScanDesk />} />
                <Route path="events" element={<Events />} />
                <Route path="events/:id" element={<EventDetail />} />
                <Route path="events/:id/sheet" element={<EventSheet />} />
                <Route path="corporates" element={<Corporates />} />
                <Route path="hotels" element={<Hotels />} />
                <Route path="participants" element={<Participants />} />
                <Route path="staff" element={<StaffAccounts />} />
                <Route path="rooms" element={<Rooms />} />
                <Route path="flights" element={<Flights />} />
                <Route path="transport" element={<Transport />} />
                <Route path="luggage" element={<LuggagePage />} />
                <Route path="attendance" element={<Attendance />} />
                <Route path="registrations" element={<Registrations />} />
                <Route path="notifications" element={<Notifications />} />
                <Route path="messages" element={<Messages />} />
                <Route path="settings" element={<Settings />} />
                <Route path="reports" element={<Reports />} />
              </Route>

              {/* ── Corporate Admin ─────────────────────────────────── */}
              <Route path="/corporate" element={<Protected roles={[ROLES.CORPORATE_ADMIN]} />}>
                <Route index element={<CorporateDashboard />} />
                <Route path="events" element={<Events />} />
                <Route path="events/:id" element={<EventDetail />} />
                <Route path="events/:id/sheet" element={<EventSheet />} />
                <Route path="participants" element={<Participants />} />
                <Route path="rooms" element={<Rooms />} />
                <Route path="flights" element={<Flights />} />
                <Route path="transport" element={<Transport />} />
                <Route path="attendance" element={<Attendance />} />
                <Route path="notifications" element={<Notifications />} />
                <Route path="messages" element={<Messages />} />
                <Route path="reports" element={<Reports />} />
              </Route>

              {/* ── Event Staff ─────────────────────────────────────── */}
              {/* Staff run their assigned events end to end; the API scopes
                  every one of these screens to the events they are on. */}
              <Route path="/staff" element={<Protected roles={[ROLES.STAFF]} />}>
                <Route index element={<StaffDashboard />} />
                <Route path="scan" element={<ScanDesk />} />
                <Route path="events" element={<Events />} />
                <Route path="events/:id" element={<EventDetail />} />
                <Route path="events/:id/sheet" element={<EventSheet />} />
                <Route path="participants" element={<Participants />} />
                <Route path="rooms" element={<Rooms />} />
                <Route path="flights" element={<Flights />} />
                <Route path="transport" element={<Transport />} />
                <Route path="luggage" element={<LuggagePage />} />
                <Route path="attendance" element={<Attendance />} />
                <Route path="notifications" element={<Notifications />} />
                <Route path="messages" element={<Messages />} />
                <Route path="reports" element={<Reports />} />
              </Route>

              {/* ── Event Viewer (company observer, read-only) ───────── */}
              <Route path="/viewer" element={<Protected roles={[ROLES.EVENT_VIEWER]} />}>
                <Route index element={<StaffDashboard />} />
                <Route path="events/:id" element={<EventDetail />} />
                <Route path="events/:id/sheet" element={<EventSheet />} />
                <Route path="participants" element={<Participants />} />
                <Route path="rooms" element={<Rooms />} />
                <Route path="flights" element={<Flights />} />
                <Route path="transport" element={<Transport />} />
                <Route path="luggage" element={<LuggagePage />} />
                <Route path="attendance" element={<Attendance />} />
                <Route path="reports" element={<Reports />} />
              </Route>

              {/* ── Attendee ───────────────────────────────────────── */}
              <Route path="/attendee" element={<Protected roles={[ROLES.USER]} gate={DeclarationGate} />}>
                <Route path="declaration" element={<AttendeeDeclaration />} />
                <Route index element={<AttendeeHome />} />
                <Route path="schedule" element={<AttendeeSchedule />} />
                <Route path="pass" element={<AttendeePass />} />
                <Route path="check-in" element={<AttendeeCheckIn />} />
                <Route path="travel" element={<AttendeeTravel />} />
                <Route path="stay" element={<AttendeeStay />} />
                <Route path="transport" element={<AttendeeTransport />} />
                <Route path="luggage" element={<AttendeeLuggage />} />
                <Route path="inbox" element={<AttendeeInbox />} />
                <Route path="profile" element={<AttendeeProfile />} />
              </Route>

              {/* ── Hotel ───────────────────────────────────────────── */}
              <Route path="/hotel" element={<Protected roles={[ROLES.HOTEL]} />}>
                <Route index element={<HotelDashboard />} />
                <Route path="rooms" element={<HotelRooms />} />
                <Route path="checkin" element={<CheckInDesk />} />
                <Route path="reports" element={<HotelReport />} />
              </Route>

              <Route path="*" element={<NotFound />} />
            </Routes>
          </EventProvider>
          </AuthProvider>
        </ToastProvider>
      </BrowserRouter>
    </MotionConfig>
  );
}
